import React, { useEffect, useMemo, useState } from 'react';
import { useThemeColors } from '@brandclub/bc-widgets';
import { useConciergeContext } from '../../ConciergeContextProvider';
import { productApi } from '../../productApi';
import { getHeadersWithUniqueVisitorId } from '../../../Auth';
import { resolveProductImageUrls } from '../../../utils/assetUtils';
import type { ConciergeProductV2Detail } from '../../types';
import type { CatalogV2Offer, CatalogV2Variant } from '../../../types/catalogV2Product';
import { useIsMobile } from '../ConciergeChatAreaV2/useIsMobile';
import { BRAND_GUTTER, DIVIDER, IMAGE_AREA_HEIGHT, IMAGE_SIZE, IMAGE_TOP_OFFSET, INK, PANEL_WIDTH } from './constants';
import { RatingRow } from './RatingRow';
import { VariantSelectors } from './VariantSelectors';
import { PriceBlock } from './PriceBlock';
import { PanelTitle } from './PanelTitle';
import { ShippingInfo } from './ShippingInfo';
import { RewardLine } from './RewardLine';
import { RetailerComparison } from './RetailerComparison';
import { KeyFeatures } from './KeyFeatures';
import { Description } from './Description';
import { CustomerConfidence } from './CustomerConfidence';
import { CloseButton } from './CloseButton';
import { resolveDisplayOffers, storeOffersToShow } from './helpers';
import { resolvePanelDetail } from './resolvePanelDetail';

function Divider() {
  return <div style={{ height: 1, backgroundColor: DIVIDER, margin: '0 16px' }} />;
}

/**
 * ChatGPT-style product detail panel.
 *
 * Two mounting modes:
 * - `sidebar` (default, full-page Concierge): an in-flow flex sibling of the
 *   chat area so opening it pushes the chat over instead of floating on top.
 *   Always mounted so the width transition animates both directions. On mobile
 *   it becomes a fixed full-viewport overlay.
 * - `overlay` (bubble widget): an absolutely positioned child of
 *   `.concierge-root` that slides in from the right and covers the ENTIRE
 *   panel — header, pinned product bar, chat — so the only way back is the X.
 *
 * Data-backed sections: image, rating, title, variant selectors, price/savings,
 * reward, and key features. The Figma also shows a multi-retailer price
 * comparison and "customer confidence" stats — those aren't available from the
 * product endpoint, so they're intentionally omitted rather than faked.
 */
export function ProductDetailPanel({ variant = 'sidebar' }: { variant?: 'sidebar' | 'overlay' } = {}) {
  const { productPanelProduct, closeProductPanel, openProductPanel, apiBaseUrl } = useConciergeContext();
  // The carousel card colors its text with the `textPrimary` theme token; mirror
  // it here by publishing `--bc-foreground` on the panel root so every `INK`
  // reference (and the filled "Add to bag" button background) resolves to the
  // same brand color instead of the black fallback.
  const { themeColors } = useThemeColors();
  const themeForeground = themeColors?.textPrimary;
  const isMobile = useIsMobile();
  const isOpen = productPanelProduct != null;
  const selectedSku = productPanelProduct?.stacklineSku ?? null;
  const detailSku = productPanelProduct?.parentStacklineSku ?? null;

  const [detail, setDetail] = useState<ConciergeProductV2Detail | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeProductPanel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, closeProductPanel]);

  useEffect(() => {
    if (!detailSku) {
      setDetail(null);
      return;
    }
    // If the carousel already preloaded this product, show it synchronously so
    // the panel opens with content instead of a blank fetch wait.
    const preloaded = productApi.getCachedWithVariants(detailSku, apiBaseUrl);
    if (preloaded) {
      setDetail(preloaded);
      return;
    }
    // Not cached yet. If this SKU is a sibling variant of what's already shown,
    // keep the current detail on screen while we fetch so switching a Size/Style
    // chip doesn't blank the panel; for a brand-new product, clear to the
    // click-provided image/title placeholder instead.
    setDetail((prev) => (prev?.stacklineSku === detailSku ? prev : null));

    let cancelled = false;
    (async () => {
      try {
        const headers = await getHeadersWithUniqueVisitorId();
        const product = await productApi.fetchWithVariantsCached({
          stacklineSku: detailSku,
          headersToInject: headers,
          apiBaseUrl,
        });
        if (!cancelled) setDetail(product);
      } catch {
        // Graceful degradation: keep the click-provided image/title only.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [detailSku, apiBaseUrl]);

  const selectedVariant =
    detail && selectedSku ? detail.variants?.find((variant) => variant.stacklineSku === selectedSku) : undefined;
  const displayDetail = useMemo(() => resolvePanelDetail(detail, selectedVariant), [detail, selectedVariant]);
  // Prefer the selected variant's own image; then the image the card/chip handed
  // off (already the selected product's image, so it's correct on a chip tap
  // before the sibling detail resolves); only then the shared family gallery.
  const imageSrc =
    selectedVariant?.imageUrl ||
    productPanelProduct?.imageUrl ||
    (displayDetail ? resolveProductImageUrls(displayDetail).src : undefined);
  const title = displayDetail?.titleCleansed || displayDetail?.title || productPanelProduct?.title || '';

  // Cross-retailer comparison (excluding the primary/current store), cheapest
  // first. Offers live at the product level for a child but on the selected family
  // member for a parent — `resolveDisplayOffers` picks the right source by kind.
  // `storeOffersToShow` then applies the app's Where to buy settings. With no
  // rows left, the whole store section hides.
  const matchedOffers = useMemo(() => {
    if (!displayDetail) return [];
    const foreign = resolveDisplayOffers(displayDetail, selectedVariant).filter(
      (offer) => !offer.isPrimary && typeof offer.retailPrice === 'number' && offer.retailPrice > 0,
    );
    return storeOffersToShow(foreign, displayDetail).sort((a, b) => (a.retailPrice ?? 0) - (b.retailPrice ?? 0));
  }, [displayDetail, selectedVariant]);

  const handleVariantSelect = (variant: CatalogV2Variant) => {
    if (!productPanelProduct) return;
    openProductPanel({
      stacklineSku: variant.stacklineSku,
      parentStacklineSku: productPanelProduct.parentStacklineSku,
      imageUrl: variant.imageUrl,
      title: variant.title,
    });
  };

  const isOverlay = variant === 'overlay';

  const modeStyles: React.CSSProperties = isOverlay
    ? {
        // Bubble widget: cover the whole panel (header + pinned bar included),
        // sliding in from the right. Kept mounted so the slide animates both ways.
        position: 'absolute',
        inset: 0,
        zIndex: 1300,
        width: '100%',
        transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        // Hidden only AFTER the slide-out finishes (the 300ms visibility delay);
        // on open the delay is dropped so the slide-in is visible immediately.
        visibility: isOpen ? 'visible' : 'hidden',
        transition: isOpen
          ? 'transform 300ms cubic-bezier(0.32, 0.72, 0, 1)'
          : 'transform 300ms cubic-bezier(0.32, 0.72, 0, 1), visibility 0s 300ms',
        willChange: 'transform',
      }
    : {
        // On mobile the panel is a full-screen overlay (fixed, full viewport)
        // instead of an in-flow sidebar that would only leave a narrow column.
        position: isMobile ? 'fixed' : 'relative',
        // Desktop needs the z-index too, not just mobile. Without it this
        // in-flow panel has `z-index: auto` and paints UNDER the concierge's
        // floating bag button (`ConciergeBagButton`, z-index 10, same corner) —
        // so "Bag · 1 / View bag / Checkout" sat on top of the panel's own
        // close button and made it unclickable.
        zIndex: 1300,
        ...(isMobile ? { top: 0, left: 0 } : {}),
        flexShrink: 0,
        width: isOpen ? (isMobile ? '100vw' : PANEL_WIDTH) : 0,
        borderLeft: isOpen && !isMobile ? `1px solid ${DIVIDER}` : 'none',
        transition: 'width 300ms cubic-bezier(0.32, 0.72, 0, 1)',
        willChange: 'width',
      };

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Product details"
      style={{
        ...modeStyles,
        height: '100%',
        overflow: 'hidden',
        backgroundColor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'var(--bc-body-font, inherit)',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        color: INK,
        ...(themeForeground ? ({ '--bc-foreground': themeForeground } as React.CSSProperties) : {}),
      }}
    >
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          overscrollBehavior: 'contain',
          width: isOverlay ? '100%' : isMobile ? '100vw' : PANEL_WIDTH,
        }}
      >
        {/* Image slider region */}
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: IMAGE_AREA_HEIGHT,
            flexShrink: 0,
            backgroundColor: '#ffffff',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            paddingTop: IMAGE_TOP_OFFSET,
          }}
        >
          {imageSrc && (
            <img
              src={imageSrc}
              alt={title}
              style={{ width: IMAGE_SIZE, height: IMAGE_SIZE, objectFit: 'contain', mixBlendMode: 'multiply' }}
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '24px 16px' }}>
          {displayDetail && <RatingRow detail={displayDetail} fallbackUnitsSold={productPanelProduct?.unitsSold} />}

          {title && <PanelTitle title={title} detail={displayDetail} />}

          {displayDetail && <VariantSelectors detail={displayDetail} onSelect={handleVariantSelect} />}
        </div>

        {displayDetail && (
          <>
            <Divider />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '16px' }}>
              <PriceBlock detail={displayDetail} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingLeft: BRAND_GUTTER }}>
                <ShippingInfo detail={displayDetail} />
                {typeof displayDetail.totalRewardAmount === 'number' && displayDetail.totalRewardAmount > 0 && (
                  <RewardLine amount={displayDetail.totalRewardAmount} />
                )}
              </div>
            </div>
          </>
        )}

        {matchedOffers.length > 0 && (
          <>
            <Divider />
            <RetailerComparison products={matchedOffers} />
          </>
        )}

        {displayDetail?.featureBullets && displayDetail.featureBullets.length > 0 ? (
          <>
            <Divider />
            <KeyFeatures bullets={displayDetail.featureBullets} />
          </>
        ) : (
          displayDetail?.description && (
            <>
              <Divider />
              <Description text={displayDetail.description} />
            </>
          )
        )}

        {false && displayDetail && (
          <>
            <Divider />
            <CustomerConfidence />
          </>
        )}
      </div>

      <CloseButton onClick={closeProductPanel} />
    </div>
  );
}
